import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo, NavigationArrow } from "@phosphor-icons/react";
import { useT } from "../lib/i18n";

const MAP_QUERY = "Yuva+Ayurvedic+Clinic+Prayagraj+Uttar+Pradesh";
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

export default function Footer() {
  const t = useT();
  const year = new Date().getFullYear();
  return (
    <footer className="bg-forest text-cream relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-sage">{t("footer.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
              Yuva Ayurvedic<br/>Clinic<br/><span className="text-cream/80">Private Limited.</span>
            </h2>
            <p className="mt-8 max-w-md text-cream/70 leading-relaxed">
              {t("footer.tagline")}
            </p>
            <p className="mt-6 max-w-md text-cream/60 text-sm leading-relaxed">
              Specialist Ayurvedic clinic in Prayagraj for men&apos;s sexual health — shighrapatan, napunsakta, mardana kamzori, sperm count and more. Classical Ayurvedic medicine, 100% confidential, no side effects. Online consultation available for NRI and international patients.
            </p>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-sage">{t("footer.nav.label")}</div>
            <ul className="mt-6 space-y-3 text-cream/80 text-sm">
              <li><Link to="/" className="hover:text-terracotta transition-colors" data-testid="footer-link-home">{t("nav.home")}</Link></li>
              <li><Link to="/about" className="hover:text-terracotta transition-colors" data-testid="footer-link-about">{t("nav.about")}</Link></li>
              <li><Link to="/treatments" className="hover:text-terracotta transition-colors" data-testid="footer-link-treatments">{t("nav.treatments")}</Link></li>
              <li><Link to="/book-appointment" className="hover:text-terracotta transition-colors" data-testid="footer-link-book">{t("nav.consultations")}</Link></li>
              <li><Link to="/blog" className="hover:text-terracotta transition-colors" data-testid="footer-link-journal">{t("nav.journal")}</Link></li>
              <li><Link to="/contact" className="hover:text-terracotta transition-colors" data-testid="footer-link-contact">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-sage">Speciality treatments</div>
            <ul className="mt-6 space-y-3 text-cream/80 text-sm">
              <li><Link to="/gupt-rog-doctor-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-guptrog">Gupt Rog Doctor Prayagraj</Link></li>
              <li><Link to="/nightfall-treatment-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-nightfall">Nightfall Treatment Prayagraj</Link></li>
              <li><Link to="/dhatu-rog-treatment-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-dhaturog">Dhatu Rog Treatment</Link></li>
              <li><Link to="/low-libido-treatment-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-libido">Low Libido Treatment</Link></li>
              <li><Link to="/low-sperm-count-treatment-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-sperm">Low Sperm Count Treatment</Link></li>
              <li><Link to="/masturbation-treatment-prayagraj" className="hover:text-terracotta transition-colors" data-testid="footer-link-masturbation">Masturbation Recovery</Link></li>
              <li><Link to="/sex-clinic-allahabad" className="hover:text-terracotta transition-colors" data-testid="footer-link-allahabad">Sex Clinic in Allahabad</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-sage">{t("footer.visit.label")}</div>
            <address className="mt-6 space-y-4 text-cream/80 text-sm not-italic" itemScope itemType="https://schema.org/PostalAddress">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-sage shrink-0" aria-hidden="true"/>
                <span>
                  Yuva Ayurvedic Clinic Private Limited,<br/>
                  <span itemProp="streetAddress">207/53, 2nd Floor, Mahatma Gandhi Marg,<br/>Subhash Chauraha, Civil Lines</span>,<br/>
                  <span itemProp="addressLocality">Prayagraj</span>,{" "}
                  <span itemProp="addressRegion">Uttar Pradesh</span>{" "}
                  <span itemProp="postalCode">211001</span>,{" "}
                  <span itemProp="addressCountry">India</span>
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-sage" aria-hidden="true"/>
                <a href="tel:+919519078666" className="hover:text-terracotta transition-colors" data-testid="footer-phone">+91 95190 78666</a>
              </div>
              <div className="flex items-center gap-3">
                <EnvelopeSimple size={18} className="text-sage" aria-hidden="true"/>
                <a href="mailto:info@yuvaayurvedic.com" className="hover:text-terracotta transition-colors" data-testid="footer-email">info@yuvaayurvedic.com</a>
              </div>
              <div className="flex items-center gap-3">
                <WhatsappLogo size={18} className="text-sage" aria-hidden="true"/>
                <a href="https://wa.me/919519078666" target="_blank" rel="noopener noreferrer" className="hover:text-terracotta transition-colors" data-testid="footer-whatsapp">{t("cta.chatWhatsapp")}</a>
              </div>
              <div className="flex items-center gap-3">
                <NavigationArrow size={18} className="text-sage" aria-hidden="true"/>
                <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta transition-colors" data-testid="footer-directions">{t("cta.getDirections")}</a>
              </div>
            </address>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-cream/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-cream/50">
          <div>© 2008–{year} Yuva Ayurvedic Clinic Private Limited. {t("footer.rights")} · Prayagraj, Uttar Pradesh, India.</div>
          <div className="flex gap-6">
            <Link to="/admin/login" className="hover:text-cream transition-colors" data-testid="admin-footer-link">{t("footer.admin")}</Link>
            <span>{t("footer.credit")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
